import {
  useOrganizationsServiceOrganizationsAccessListKey,
  useOrganizationsServiceOrganizationsAccessPartialUpdate,
  useUsersServiceUsersPartialUpdate
} from '@organization/api/queries'
import { OrganizationRole, OrganizationUsers } from '@organization/api/requests'
import { useValidationsFields } from '@organization/features/users/hooks'
import { PhoneInput } from '@shared/components'
import { useInvalidateQueries } from '@shared/hooks'
import { replaceEmptyWithNull, showApiErrors } from '@shared/utils'
import { App, Form, Input, Modal, Select } from 'antd'
import { useTranslation } from 'react-i18next'

type Props = {
  user?: OrganizationUsers | null
  onCancel: () => void
}

export const UserRole = ({ user, onCancel }: Props) => {
  const [form] = Form.useForm()
  const { message } = App.useApp()

  const { required, personalCode } = useValidationsFields()

  const { t } = useTranslation()
  const { invalidateQueries } = useInvalidateQueries()

  const { mutateAsync: accessMutate, isPending: isAccessPending } =
    useOrganizationsServiceOrganizationsAccessPartialUpdate({
      onError: (e) => showApiErrors(e, form)
    })

  const { mutateAsync: userMutate, isPending: isUserPending } =
    useUsersServiceUsersPartialUpdate({
      onError: (e) => showApiErrors(e, form)
    })

  const onSubmit = async () => {
    const values = await form.validateFields()

    if (user?.role !== values.role)
      await accessMutate({
        id: Number(user?.id),
        requestBody: {
          role: values.role
        }
      })

    if (
      user?.user?.first_name !== values?.user?.first_name ||
      user?.user?.last_name !== values?.user?.last_name ||
      user?.user?.phone !== values?.user?.phone ||
      user?.user?.identification_number !== values?.user?.identification_number
    )
      await userMutate({
        id: Number(user?.user?.id),
        requestBody: replaceEmptyWithNull(values?.user)
      })

    message.success(t('users.user_edited'))
    invalidateQueries([useOrganizationsServiceOrganizationsAccessListKey])
    onCancel()
  }

  const isPending = isAccessPending || isUserPending

  return (
    <Modal
      title={t('modal.edit_user')}
      open
      onOk={onSubmit}
      onCancel={onCancel}
      destroyOnHidden
      okButtonProps={{
        loading: isPending
      }}
    >
      <Form layout="vertical" form={form} initialValues={{ ...user }}>
        <Form.Item
          name="role"
          label={t('form.item.user_role')}
          rules={[
            {
              required: true
            }
          ]}
        >
          <Select
            options={[
              {
                label: t('form.item.administrator'),
                value: OrganizationRole.ADMIN
              },
              {
                label: t('form.item.employee'),
                value: OrganizationRole.EMPLOYEE
              }
            ]}
            placeholder={t('form.placeholder.select_user_role')}
          />
        </Form.Item>

        <Form.Item
          name={['user', 'last_name']}
          label={t('form.item.last_name')}
          rules={[{ required: true, message: t('form.message.last_name') }]}
        >
          <Input autoComplete="off" placeholder={t('form.message.last_name')} />
        </Form.Item>

        <Form.Item
          name={['user', 'first_name']}
          label={t('form.item.first_name')}
          rules={[{ required: true, message: t('form.message.first_name') }]}
        >
          <Input
            autoComplete="off"
            placeholder={t('form.message.first_name')}
          />
        </Form.Item>

        <Form.Item name={['user', 'phone']} label={t('form.item.phone')}>
          <PhoneInput />
        </Form.Item>

        <Form.Item
          name={['user', 'identification_number']}
          label={t('identification_number')}
          normalize={(value, prevValue) =>
            String(value).length > 13 ? prevValue : value
          }
          rules={[required, personalCode]}
        >
          <Input
            type="number"
            min={0}
            placeholder={t('identification_number')}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
